import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CircularProgress, Typography, Alert, AlertTitle } from '@mui/material';

import { useAuth } from '../contexts/AuthContext';
import { useWaterQualityData } from '../hooks/useWaterQualityData';
import { RISK_TIERS, TIER_ORDER } from '../utils/riskTier';

import CurrentStatusTab from '../components/hab/CurrentStatusTab';
import WaterQualityDataTab from '../components/hab/WaterQualityDataTab';
import { anonymizeGrid } from '../utils/demo';
import { buoyNumbersById, buoyLabel, isActiveMooring } from '../utils/moorings';

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700;9..144,800&family=Outfit:wght@300;400;500;600;700&display=swap');
  @keyframes spin { to { transform: rotate(360deg); } }
  @media (max-width: 880px) { .cs-elevated-grid { grid-template-columns: 1fr !important; } }
`;

const formatDateTime = (iso) => {
  if (!iso) return 'Unknown';
  const d = new Date(iso);
  const formatted = d.toLocaleString('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
  const tz = d.toLocaleString('en-US', { timeZone: 'America/New_York', timeZoneName: 'short' }).split(' ').pop();
  return `${formatted} ${tz}`;
};

const getStatusDotColor = (lastUpdate) => {
  if (!lastUpdate) return '#f44336';
  const hrs = (Date.now() - new Date(lastUpdate)) / 3600000;
  return hrs < 2 ? '#22c55e' : hrs < 6 ? '#ff9800' : '#f44336';
};

export default function HABDashboardPage() {
  const { 'grid-id': gridId } = useParams();
  const navigate = useNavigate();
  // Which buoy in the grid to show. Grids can hold several buoys, so the buoy is
  // part of the address — without it a link to a multi-buoy grid always lands on
  // whichever buoy the API happens to return first.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedBuoyId = searchParams.get('buoy');
  const { grids, gridsLoading, token, user, demoMode } = useAuth();
  const isAdmin = !!(user?.is_admin);
  const demoActive = isAdmin && demoMode;
  // In demo mode, admins preview exactly what a standard user sees.
  const effectiveIsAdmin = demoActive ? false : isAdmin;

  const [rangeKey, setRangeKey] = useState('3d');

  const realGridData = grids.find(g => g.id === parseInt(gridId));
  // Identifiable fields are swapped for stable fakes in demo mode. Data fetching
  // still keys off the real gridId (from the URL), so only display changes.
  const gridData = demoActive ? anonymizeGrid(realGridData) : realGridData;

  const {
    transformedData, habRisk, habRisk7Day, loading, loadingHabRisk, loadingPriority2, loadingPriority3,
    error, latestSync, unmooredAt, latestLatLon, launchDate, rangeLabel, startDateISO, endDateISO,
    moorings, mooringId,
  } = useWaterQualityData({
    gridId,
    rangeKey,
    token,
    mooringId: requestedBuoyId,
    enabled: !gridsLoading && !!gridData,
  });

  const buoyNumbers = useMemo(() => buoyNumbersById(moorings), [moorings]);
  const selectBuoy = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('buoy', String(id));
    setSearchParams(next, { replace: true });
  };

  const isGridActive = !!(unmooredAt == null && latestSync && new Date(latestSync).getFullYear() === new Date().getFullYear());
  const [mainTab, setMainTab] = useState('status');

  // Only redirect to data tab once loading resolves and the grid is confirmed inactive.
  // Defaulting to 'status' prevents a flash to 'data' while latestSync is still null.
  useEffect(() => {
    if (!loading && !loadingHabRisk && !isGridActive) setMainTab('data');
  }, [loading, loadingHabRisk, isGridActive]);

  // For inactive buoys, default the range to "Since Launch" so a user landing on
  // the data tab immediately sees the full deployment history rather than a recent
  // window with no data. Guarded by a ref so it only fires once — a later manual
  // range change shouldn't get clobbered by this effect re-running.
  const autoSelectedLaunchRange = useRef(false);
  useEffect(() => {
    if (!loading && !loadingHabRisk && !isGridActive && !autoSelectedLaunchRange.current) {
      autoSelectedLaunchRange.current = true;
      setRangeKey('launch');
    }
  }, [loading, loadingHabRisk, isGridActive]);

  const tierKey = habRisk?.risk_tier ? TIER_ORDER[habRisk.risk_tier - 1] : 'BASELINE';
  const pcTrend = habRisk?.pc_trend || 'stable';

  const haveInternalWind = !!(transformedData.wind_direction_true?.length && transformedData.wind_speed?.length);
  const haveExternalWind = !!(transformedData.wind_direction_ext?.length && transformedData.wind_speed_ext?.length);

  const tc = RISK_TIERS[tierKey] || RISK_TIERS.BASELINE;

  // Only the grid list gates the whole page. A buoy's own risk/readings load
  // inside the content area below, so switching buoys doesn't tear down the
  // header — and the switcher you just clicked — on every click.
  if (gridsLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 64, minHeight: '100vh', gap: 16 }}>
        <CircularProgress size={24} />
        <Typography>Loading pond data…</Typography>
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        <AlertTitle>Error</AlertTitle>{error}
      </Alert>
    );
  }

  if (!gridData) {
    return <Alert severity="info" sx={{ m: 3 }}>Grid not found.</Alert>;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f6f5f1', fontFamily: "'Outfit',sans-serif", color: '#1e293b', paddingTop: 64 }}>
      <style>{GLOBAL_STYLES}</style>

      {/* Sticky header: back nav + title + tab nav */}
      <div style={{ position: 'sticky', top: 64, zIndex: 100, background: '#fff', borderBottom: '1px solid #e8e4df' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', padding: '14px 24px 0' }}>
          {/* Back nav */}
          <button
            onClick={() => navigate('/ponds')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#6f6a5f', fontSize: 14, fontWeight: 600,
              fontFamily: 'inherit', padding: 0, marginBottom: 10,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#1f2a2e')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#6f6a5f')}
          >
            ← All lakes
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, maxWidth: mainTab === 'status' && tierKey === 'BASELINE' ? 820 : undefined }}>
            <div>
              <h1 style={{ fontFamily: "'Fraunces',Georgia,serif", fontSize: 26, fontWeight: 700, letterSpacing: '-.03em', margin: 0, color: '#1f2a2e' }}>
                {gridData.name}{gridData.qualifier ? <span style={{ fontWeight: 500, color: '#9a9384', marginLeft: 8 }}>{gridData.qualifier}</span> : null}
              </h1>
              <p style={{ margin: '5px 0 0', fontSize: 13.5, color: '#8b8475', display: 'flex', alignItems: 'center', gap: 6 }}>
                {/* The sync time comes from the buoy list. Until that lands there's
                    no status to report yet — showing the "no data" red dot while
                    still loading would read as an outage. */}
                {latestSync || !loadingHabRisk ? (
                  <>
                    <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: getStatusDotColor(latestSync), flexShrink: 0 }} />
                    Updated {formatDateTime(latestSync)}
                  </>
                ) : 'Loading…'}
                {gridData.city ? ` · ${gridData.city}${gridData.region ? `, ${gridData.region}` : ''}` : ''}
              </p>

              {/* Buoy switcher — a grid can hold several buoys, and every reading on
                  this page belongs to exactly one of them. Only shown when there's
                  a choice to make. */}
              {moorings.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 11, flexWrap: 'wrap' }}>
                  {moorings.map(m => {
                    const selected = m.id === mooringId;
                    const active = isActiveMooring(m);
                    return (
                      <button
                        key={m.id}
                        onClick={() => selectBuoy(m.id)}
                        title={active ? undefined : 'No recent data — this buoy is not currently deployed'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '6px 13px', borderRadius: 999, cursor: 'pointer',
                          fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600,
                          background: selected ? '#1f2a2e' : '#fff',
                          color: selected ? '#fff' : '#6f6a5f',
                          border: `1px solid ${selected ? '#1f2a2e' : '#e3ddcf'}`,
                          transition: 'background .12s, color .12s',
                        }}
                      >
                        <span style={{
                          width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                          background: active ? '#22c55e' : '#b8b1a2',
                        }} />
                        {buoyLabel(m, buoyNumbers[m.id], demoActive)}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Held back until risk resolves — tierKey defaults to BASELINE while
                loading, and flashing "Baseline" before the real tier arrives would
                understate the risk. */}
            {!loadingHabRisk && isGridActive && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 15px', borderRadius: 100, background: tc.bg, border: `1px solid ${tc.border}`, flexShrink: 0, marginTop: 4 }}>
                <div style={{ width: 9, height: 9, borderRadius: '50%', background: tc.solid }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: tc.color }}>{tc.label}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tab nav */}
        <div style={{ maxWidth: 1120, margin: '0 auto', padding: '0 24px', display: 'flex', gap: 28 }}>
          {[isGridActive && ['status', 'Current Status'], ['data', 'Water Quality Data']].filter(Boolean).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setMainTab(key)}
              style={{
                padding: '12px 0', fontSize: 15.5, background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: 'inherit',
                fontWeight: mainTab === key ? 700 : 500,
                color: mainTab === key ? '#1f2a2e' : '#a59d8b',
                borderBottom: mainTab === key ? '2.5px solid #1f2a2e' : '2.5px solid transparent',
                marginBottom: -1, transition: 'color .12s',
              }}
            >{label}</button>
          ))}
        </div>
      </div>

      {/* Shared outer container — both tabs share the same left origin */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '24px 24px 0' }}>
        {loadingHabRisk && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '64px 0' }}>
            <CircularProgress size={24} />
            <Typography>Loading pond data…</Typography>
          </div>
        )}

        {!loadingHabRisk && mainTab === 'status' && (
          <CurrentStatusTab
            tierKey={tierKey}
            pcTrend={pcTrend}
            habRisk={habRisk}
            habRisk7Day={habRisk7Day}
            loadingHabRisk={loadingHabRisk}
            transformedData={transformedData}
            latestLatLon={latestLatLon}
            gridData={gridData}
            isAdmin={effectiveIsAdmin}
            demoMode={demoActive}
            rangeLabel={rangeLabel}
            haveInternalWind={haveInternalWind}
            haveExternalWind={haveExternalWind}
          />
        )}

        {!loadingHabRisk && mainTab === 'data' && (
          <WaterQualityDataTab
            transformedData={transformedData}
            gridId={gridId}
            rangeKey={rangeKey}
            setRangeKey={setRangeKey}
            rangeLabel={rangeLabel}
            launchDate={launchDate}
            startDateISO={startDateISO}
            endDateISO={endDateISO}
            isAdmin={effectiveIsAdmin}
            loadingPriority1={loading}
            loadingPriority2={loadingPriority2}
            loadingPriority3={loadingPriority3}
            dataError={error}
          />
        )}
      </div>

      <footer style={{ background: '#fff', borderTop: '1px solid #e8e4df', padding: '22px 0', marginTop: 28 }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', padding: '0 24px' }}>
          <p style={{ fontSize: 14, color: '#6f6a5f', lineHeight: 1.6, margin: '0 0 6px', maxWidth: 820 }}>
            <strong style={{ color: '#46505a' }}>Important:</strong> This system estimates the probability of harmful toxin levels using continuous monitoring, community reports, and lab results. It estimates toxin risk but cannot measure toxins directly. If you see scum or green water, avoid contact regardless of what the app shows.
          </p>
          <p style={{ fontSize: 12.5, color: '#b0a896', margin: '6px 0 0' }}>
            HAB Risk Model v1 (early release, validating against lab samples) · {gridData.name}{gridData.qualifier ? ` ${gridData.qualifier}` : ''} · Powered by Subtidal
          </p>
        </div>
      </footer>
    </div>
  );
}
